import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { GiroEncima, APRETADO, IconoDeVentana, OPACIDAD_APAGADO } from './alumno';
import { FONDO_DE_LA_VENTANA, BOTON_VENTANA_PRINCIPAL, BOTON_VENTANA_SECUNDARIO } from './panel/VentanaDelPanel';
import { AreaDe44 } from './panel/anatomia';
import { useHojaDeLaCasa, FilaDeAgarradera, ENTRA_EL_VELO, ENTRA_LA_CAJA } from './alumno/arrastreDeHoja';

/* \u2500\u2500 #433 / #434 \u2014 LA VENTANA DEL MOTIVO \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
 *
 * UNA sola ventana para las puertas de cancelar Pro, porque es la misma
 * pregunta: por que te vas. Lo que cambia entre ellas es el boton primario, y
 * se decide por `variante`:
 *
 *   `web`   -> "Cancelar mi plan". Detras de este boton hay una cancelacion de
 *              verdad.
 *   `app`   -> "Continuar a Apple". Detras de este boton no cancelamos nada: se
 *              abre la hoja del sistema y cancela Apple.
 *
 * LA REGLA DEL TEXTO ES UNA SOLA PARA LAS DOS (remates-1-2-1, B). Con `feature` y
 * `other` el texto es OBLIGATORIO (>= LARGO_MINIMO caracteres despues de
 * `trim()`); con los otros cuatro es opcional. La decide `puedeContinuar`, y la
 * usan las dos puertas.
 *
 * SIN `<form>` A PROPOSITO: un `<form>` dentro del arbol de Ajustes hereda el
 * submit de la pagina y un Enter en la caja de texto dispararia la cancelacion.
 * El boton primario es el UNICO camino.
 *
 * LOS SEIS MOTIVOS SON BOTONES, no un `<select>`: en un telefono un desplegable
 * nativo tapa la ventana entera y el motivo se elige a ciegas. Van en un
 * `role="radiogroup"` con `aria-checked`.
 *
 * pantallas-alumno (G), AJ3 de la parte 2: LA VENTANA DEL PANEL DE 560 (ver el
 * bloque del `return`). El contenido, los textos y la regla son los de hoy.
 */

/** Los seis codigos, EN ESTE ORDEN. El servidor valida contra esta misma lista. */
export const MOTIVOS = ['expensive', 'unused', 'errors', 'done', 'feature', 'other'];

/** Los motivos que exigen contarnos algo, en las DOS variantes (remates-1-2-1, B). */
export const MOTIVOS_CON_TEXTO = ['feature', 'other'];

/** Minimo de caracteres del texto cuando se exige. */
export const LARGO_MINIMO = 10;

/** Tope del texto. El servidor RECHAZA por encima de este numero (#448).
 *
 * Era 500 y el servidor recortaba en silencio: quien escribia de mas perdia el
 * final de su comentario sin enterarse. Hoy son 2,000, el contador de abajo dice
 * cuanto llevas, y si algo llegara por encima (la app, un cliente de API) el
 * servidor contesta 400 `reason_text_too_long` con su aviso en los dos idiomas.
 * El mismo numero vive en `backend/services/cancelaciones.py::LARGO_MAXIMO`, y
 * una prueba del backend compara los dos literales. */
export const LARGO_MAXIMO = 2000;

/** \u00ab1,234\u00bb \u2014 el contador se lee IGUAL en los dos idiomas (Anexo A de #448), asi
 *  que el separador de miles se escribe a mano y no con `toLocaleString`, que
 *  cambia con el idioma del navegador. */
export function conMiles(n) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * LA UNICA funcion que decide si el boton primario puede pulsarse, en las DOS
 * puertas. Exportada para que la prueba mida la REGLA y no el pintado.
 *
 * remates-1-2-1 (B): la `variante` YA NO CUENTA. El parametro se conserva para
 * que ningun llamador cambie de forma; la regla es la misma para los dos.
 */
export function puedeContinuar(variante, motivo, texto) {
  if (!motivo || MOTIVOS.indexOf(motivo) === -1) return false;
  if (MOTIVOS_CON_TEXTO.indexOf(motivo) === -1) return true;
  return String(texto || '').trim().length >= LARGO_MINIMO;
}

/* remates-2 (C1) -- EL BOTON APAGADO EXPLICA POR QUE.
 *
 * El primario se sigue VIENDO apagado mientras falte algo, pero no lleva
 * `disabled` por eso: lleva `aria-disabled` y recibe el toque, y el toque pinta
 * el banner del caso y no manda nada. `disabled` queda SOLO para la peticion en
 * vuelo, donde un toque no hace NADA.
 *
 * `avisoDelBoton` dice que aviso le toca a cada caso, con las MISMAS tres ramas de
 * `puedeContinuar` (una prueba fija que no pueden discrepar):
 *   . sin motivo, o un codigo que no es de los seis   -> `reason_required`;
 *   . motivo que exige texto y menos de LARGO_MINIMO  -> `reason_text_required`;
 *   . todo en orden                                   -> null (el boton manda). */
export function avisoDelBoton(motivo, texto) {
  if (!motivo || MOTIVOS.indexOf(motivo) === -1) return 'reason_required';
  if (MOTIVOS_CON_TEXTO.indexOf(motivo) === -1) return null;
  return String(texto || '').trim().length >= LARGO_MINIMO ? null : 'reason_text_required';
}

/** El `id` del banner: tocar tres veces no apila tres avisos, el nuevo reemplaza al
 *  anterior (sonner actualiza el aviso vivo que lleva el mismo `id`). */
export const AVISO_ID = 'cancelar-motivo';

export default function CancelarConMotivo({
  open,
  variante = 'web',
  loading = false,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation(['settings', 'common']);
  const [motivo, setMotivo] = useState('');
  const [texto, setTexto] = useState('');
  const primeroRef = useRef(null);

  // Al abrir: el foco entra a la ventana (si no, el lector de pantalla se queda
  // en el boton de la pagina de atras) y la eleccion anterior se olvida.
  // SIN `setTimeout`: el efecto corre despues del montaje, asi que la ref ya
  // apunta al primer motivo.
  useEffect(() => {
    if (!open) return;
    setMotivo('');
    setTexto('');
    if (primeroRef.current) primeroRef.current.focus();
  }, [open]);

  // pantallas-alumno (G): Esc cierra, como la ventana del Panel; mientras la peticion vuela, no.
  const cerrarRef = useRef(onClose);
  cerrarRef.current = onClose;
  const ocupadaRef = useRef(loading);
  ocupadaRef.current = loading;
  useEffect(() => {
    if (!open) return undefined;
    const tecla = (e) => { if (e.key === 'Escape' && !ocupadaRef.current && cerrarRef.current) cerrarRef.current(); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [open]);
  // marca-1 (#726): la agarradera cierra como Esc (mientras la peticion vuela, no: la hoja regresa) y la hoja entra
  // y sale como las demas.
  const hoja = useHojaDeLaCasa(onClose, { abierta: !!open, cerrable: !loading });

  if (!open) return null;

  const esApp = variante === 'app';
  // remates-1-2-1 (B): la misma regla en las dos puertas (ver `puedeContinuar`).
  const exigeTexto = MOTIVOS_CON_TEXTO.indexOf(motivo) !== -1;
  const listo = puedeContinuar(variante, motivo, texto);

  const confirmar = () => {
    if (loading) return;
    // remates-2 (C1): el mismo banner que Ajustes pinta cuando el servidor contesta
    // 400 `reason_required` -- `toast.error` del chasis -- con su `id` fijo.
    const aviso = avisoDelBoton(motivo, texto);
    if (aviso) {
      toast.error(t(`common:errors.${aviso}`), { id: AVISO_ID });
      return;
    }
    onConfirm(motivo, String(texto || '').trim().slice(0, LARGO_MAXIMO));
  };

  /* pantallas-alumno (G), AJ3 de la parte 2: LA VENTANA DEL PANEL DE 560.
   * El velo de la casa (`FONDO_DE_LA_VENTANA`: fijo en los cuatro bordes, negro al 66 %, z 70, por encima de la
   * barra de arriba) y la caja de la ventana H (#161616, borde #2A2A2A, radio 20; en el celular hoja desde abajo
   * con agarradera y radio 24 arriba). Sigue en un PORTAL a `body` (C1 de remates-1-2-1) y sigue partida en
   * CABEZA (fija: la X de hoy, el triangulo en cuadro naranja suave, el titulo y el texto), CUERPO (lo unico que
   * se desplaza: los seis motivos, la caja y el contador) y PIE (fijo: los dos botones; en el celular a lo ancho
   * y el principal ARRIBA, desde 768 en fila a la derecha). La caja mide como maximo 90 % del alto de la pantalla y el
   * pie guarda la zona segura de abajo (la barrita de inicio), como la ventana del Panel.
   * C3 (sin zoom en el iPhone): la caja de texto va a 16 px en el celular. */
  return createPortal(
    <div ref={hoja.veloRef} className={`${FONDO_DE_LA_VENTANA} ${ENTRA_EL_VELO}`} data-testid="cancel-reason-modal">
      <div ref={hoja.cajaRef} className={`relative w-full md:max-w-[560px] max-h-[90dvh] flex flex-col bg-[#161616] border border-[#2A2A2A] rounded-t-[24px] md:rounded-[20px] shadow-2xl overflow-hidden ${ENTRA_LA_CAJA}`}
        role="dialog" aria-modal="true" aria-labelledby="cancel-reason-title"
        data-testid="cancel-reason-panel">
        <FilaDeAgarradera agarradera={hoja.agarradera} testid="cancel-reason-agarradera" />
        <button type="button" onClick={onClose} aria-label={t('common:cancel')} title={t('common:cancel')}
          className="absolute z-[2] top-2.5 right-3 md:top-4 md:right-4 w-10 h-10 rounded-xl flex items-center justify-center text-[#9A9A9A] hoverable:text-white hoverable:bg-[#1B1B1B] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          data-testid="cancel-reason-close">
          <AreaDe44 />
          <X className="w-5 h-5" />
        </button>
        <div className="shrink-0 px-6 pt-4 md:pt-6 pb-4 pr-14" data-testid="cancel-reason-head">
          <IconoDeVentana tono="naranja" icono={AlertTriangle} testid="cancel-reason-icono" />
          <h3 id="cancel-reason-title" className="mt-4 text-[17px] leading-snug font-semibold text-[#F4F4F3] break-words"
            data-testid="cancel-reason-heading">
            {t('settings:cancel.title')}
          </h3>
          <p className="mt-1 text-[14px] leading-5 text-[#9A9A9A]">{t('settings:cancel.subtitle')}</p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6"
          data-testid="cancel-reason-body">
          <div role="radiogroup" aria-label={t('settings:cancel.subtitle')} className="flex flex-col gap-2 mb-5">
            {MOTIVOS.map((code, i) => {
              const elegido = motivo === code;
              return (
                <button
                  key={code}
                  ref={i === 0 ? primeroRef : null}
                  type="button"
                  role="radio"
                  aria-checked={elegido}
                  onClick={() => setMotivo(code)}
                  className={`w-full flex items-center gap-3 text-left min-h-[48px] px-3.5 py-2.5 rounded-xl text-[14px] leading-5 border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                    elegido
                      ? 'border-[#F27A2E] bg-[#17100B] text-[#F7A06A]'
                      : 'border-[#262626] bg-[#101010] text-[#C8C8C8] hoverable:border-[#3A3A3A]'
                  }`}
                  data-testid={`cancel-reason-${code}`}
                >
                  <span aria-hidden="true" data-parte="circulo"
                    className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${elegido ? 'border-[#F27A2E]' : 'border-[#4A4A4A]'}`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${elegido ? 'bg-[#F27A2E]' : 'bg-transparent'}`} />

                  </span>
                  <span className="min-w-0">{t(`settings:cancel.reason.${code}`)}</span>
                </button>
              );
            })}
          </div>

          <label className="block text-[13px] font-semibold text-[#9A9A9A] mb-1.5" htmlFor="cancel-reason-text">
            {exigeTexto ? t('settings:cancel.text_required') : t('settings:cancel.text_optional')}
          </label>
          {/* remates-2 (B) -- LA PISTA VA DENTRO DE LA CAJA (`placeholder`), y SOLO con un motivo que exige texto:
              ningun nodo entra ni sale al cambiar de motivo. pantallas-alumno (G): la caja larga de la casa, de
              alto fijo (110) y que se desplaza por dentro. */}
          <textarea
            id="cancel-reason-text"
            value={texto}
            maxLength={LARGO_MAXIMO}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={exigeTexto ? t('settings:cancel.text_hint') : undefined}
            className="w-full h-[110px] resize-none overflow-y-auto px-3.5 py-3 rounded-[14px] bg-[#0E0E0E] border border-[#262626] text-[16px] md:text-[15px] leading-snug text-[#F4F4F3] placeholder:text-muted-foreground outline-none focus:border-[#F27A2E]"
            data-testid="cancel-reason-text"
          />
          {/* #448 -- EL CONTADOR, a la derecha; al tope el numero se pinta naranja (el naranja de texto del Panel).
              Mismo formato en los dos idiomas. */}
          <p className="text-[13px] text-[#9A9A9A] mt-1.5 text-right tabular-nums"
            data-testid="cancel-reason-counter">
            <span className={texto.length >= LARGO_MAXIMO ? 'text-[#F7A06A]' : undefined}>
              {conMiles(texto.length)}
            </span>
            {' / '}{conMiles(LARGO_MAXIMO)}
          </p>
          {esApp && (
            <p className="text-[14px] text-[#C8C8C8] mt-4 leading-5" data-testid="cancel-reason-app-note">
              {t('settings:cancel.app_note')}
            </p>
          )}
        </div>

        {/* El pie, en el orden de la computadora: el secundario ("Cancel my plan" / "Continue to Apple") y luego el
            principal ("Keep Pro"); en el celular apila AL REVES, asi que "Keep Pro" queda arriba. remates-2 (C1):
            apagado por el motivo = `aria-disabled` (recibe el toque y pinta el aviso del caso), al 45 %; `disabled`
            queda SOLO para la peticion en vuelo, que ademas pone el giro encima sin cambiar el ancho. */}
        <div className="shrink-0 px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:pb-6 flex flex-col-reverse md:flex-row md:justify-end gap-2"
          data-testid="cancel-reason-foot">
          <button type="button" onClick={confirmar} disabled={loading}
            aria-disabled={listo ? undefined : 'true'}
            aria-busy={loading || undefined}
            style={listo || loading ? undefined : { opacity: OPACIDAD_APAGADO }}
            className={`relative ${BOTON_VENTANA_SECUNDARIO} ${listo ? APRETADO : 'cursor-default'}`}
            data-testid="cancel-reason-confirm">
            <GiroEncima trabajando={loading}>
              {esApp ? t('settings:cancel.confirm_app') : t('settings:cancel.confirm_web')}
            </GiroEncima>
          </button>
          <button type="button" onClick={onClose} disabled={loading}
            className={`${BOTON_VENTANA_PRINCIPAL} ${APRETADO}`}
            data-testid="cancel-reason-keep">
            {t('settings:cancel.keep')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
